import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence, MotionConfig } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { Debate } from '../types';

const EASE: [number, number, number, number] = [0.22, 1, 0.36, 1];

const formatRelativeTime = (date: Date): string => {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

const isLive = (status: string) => status === 'live' || status === 'active';
const isUpcoming = (status: string) => status === 'upcoming' || status === 'pending';

/**
 * Editorial debate row — the replacement for the card grid.
 * Topic text gets full column width so nothing truncates.
 * For/Against bar uses the same semantic colors as the landing page.
 */
const DebateRow: React.FC<{ debate: Debate; onClick: () => void }> = ({ debate, onClick }) => {
  const total = debate.voteCount;
  const proShare = total > 0 ? ((debate.proVotes || 0) / total) * 100 : 50;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.35, ease: EASE }}
      onClick={onClick}
      className="group flex cursor-pointer items-start gap-4 px-5 py-6 transition-colors hover:bg-black/[0.025] sm:px-7"
    >
      {/* Live / status indicator */}
      <div className="mt-[7px] flex-shrink-0">
        <span
          className={`block h-2 w-2 rounded-full ${
            isLive(debate.status)
              ? 'bg-steel-blue animate-pulse'
              : isUpcoming(debate.status)
              ? 'bg-amber-400'
              : 'bg-charcoal/20'
          }`}
        />
      </div>

      {/* Main content */}
      <div className="min-w-0 flex-1">
        <h3 className="text-pretty font-serif text-lg font-semibold leading-snug text-charcoal transition-colors group-hover:text-steel-blue">
          {debate.topic}
        </h3>

        <p className="mt-1 text-xs text-ink-muted">
          {debate.createdBy && <span>by {debate.createdBy}</span>}
          {debate.createdAt && (
            <span className="ml-2 font-mono">· {formatRelativeTime(debate.createdAt)}</span>
          )}
          {isLive(debate.status) && (
            <span className="ml-2 font-mono font-semibold text-steel-blue">· Live</span>
          )}
        </p>

        {/* Vote bar — For/Against semantic colors only */}
        <div className="mt-3 max-w-xs">
          <div className="flex h-1 overflow-hidden rounded-full bg-hairline">
            <div className="bg-steel-blue" style={{ width: `${proShare}%` }} />
            <div className="ml-auto bg-vermilion" style={{ width: `${100 - proShare}%` }} />
          </div>
          <div className="mt-1.5 flex items-center gap-3 text-xs">
            <span className="font-mono font-medium text-steel-blue">{debate.proVotes || 0} for</span>
            <span className="font-mono font-medium text-vermilion">{debate.conVotes || 0} against</span>
            {total > 0 && (
              <span className="font-mono text-ink-muted">· {total.toLocaleString()} votes</span>
            )}
          </div>
        </div>
      </div>

      {/* Arrow — only decoration, click is on the whole row */}
      <div className="mt-1 flex-shrink-0 text-ink-muted transition-transform group-hover:translate-x-0.5 group-hover:text-steel-blue">
        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M17 8l4 4m0 0l-4 4m4-4H3"
          />
        </svg>
      </div>
    </motion.div>
  );
};

const STATUS_TABS = [
  { value: 'all', label: 'All' },
  { value: 'live', label: 'Live' },
  { value: 'upcoming', label: 'Upcoming' },
  { value: 'finished', label: 'Finished' },
] as const;

const Explore: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [debates, setDebates] = useState<Debate[]>([]);
  const [filteredDebates, setFilteredDebates] = useState<Debate[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const pageSize = 12;

  const isMountedRef = useRef<boolean>(true);

  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('newest');
  const [searchQuery, setSearchQuery] = useState<string>('');

  const [stats, setStats] = useState({ totalDebates: 0, totalVotes: 0 });

  const fetchPublicDebates = useCallback(async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase.rpc('get_public_debates');
      if (error) throw error;

      let userVotes: Record<string, 'pro' | 'con'> = {};
      if (user) {
        const { data: votesData, error: votesError } = await supabase
          .from('votes')
          .select('debate_id, vote_for')
          .eq('user_id', user.id);
        if (!votesError && votesData) {
          userVotes = votesData.reduce((acc, vote) => {
            acc[vote.debate_id] = vote.vote_for;
            return acc;
          }, {} as Record<string, 'pro' | 'con'>);
        }
      }

      const transformedDebates: Debate[] = (data || []).map((d: any) => ({
        id: d.id,
        topic: d.topic,
        description: '',
        status: d.status as 'live' | 'upcoming' | 'finished',
        sideA: { position: d.pro_agent_name || 'Supporting', color: '#3A68A0' },
        sideB: { position: d.con_agent_name || 'Opposing', color: '#E5442D' },
        voteCount: d.total_votes || 0,
        totalRounds: 3,
        tags: [],
        createdAt: new Date(d.created_at),
        startedAt: d.created_at ? new Date(d.created_at) : undefined,
        finishedAt: d.completed_at ? new Date(d.completed_at) : undefined,
        createdBy: d.username || d.display_name || 'Anonymous',
        proVotes: d.pro_votes || 0,
        conVotes: d.con_votes || 0,
        userVote: userVotes[d.id] || null,
      }));

      if (isMountedRef.current) {
        setDebates(transformedDebates);
        const totalVotes = transformedDebates.reduce((acc, d) => acc + d.voteCount, 0);
        setStats({ totalDebates: transformedDebates.length, totalVotes });
      }
    } catch (error) {
      console.error('Error fetching debates:', error);
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  useEffect(() => { fetchPublicDebates(); }, [fetchPublicDebates]);

  useEffect(() => {
    let filtered = [...debates];

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(
        d =>
          d.topic.toLowerCase().includes(q) ||
          d.sideA.position.toLowerCase().includes(q) ||
          d.sideB.position.toLowerCase().includes(q) ||
          d.createdBy?.toLowerCase().includes(q),
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(d => {
        if (statusFilter === 'live') return isLive(d.status);
        if (statusFilter === 'upcoming') return isUpcoming(d.status);
        if (statusFilter === 'finished') return d.status === 'finished' || d.status === 'completed';
        return true;
      });
    }

    if (sortBy === 'newest') {
      filtered.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
    } else if (sortBy === 'most_voted') {
      filtered.sort((a, b) => b.voteCount - a.voteCount);
    }

    setFilteredDebates(filtered);
  }, [debates, statusFilter, sortBy, searchQuery]);

  useEffect(() => { setCurrentPage(1); }, [debates, statusFilter, sortBy, searchQuery]);

  const totalPages = Math.max(1, Math.ceil(filteredDebates.length / pageSize));
  const startIdx = (currentPage - 1) * pageSize;
  const pageDebates = filteredDebates.slice(startIdx, startIdx + pageSize);

  const goToPage = (p: number) => {
    if (p < 1 || p > totalPages) return;
    setCurrentPage(p);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <MotionConfig reducedMotion="user">
      <div className="relative isolate w-full min-h-screen bg-white">
        {/* Blueprint grid — same canvas as landing */}
        <div aria-hidden className="blueprint-grid pointer-events-none absolute inset-0 z-0" />

        <div className="relative z-10 mx-auto max-w-4xl px-6 py-14 sm:py-20">

          {/* ── Page header ── */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: EASE }}
            className="flex items-end justify-between gap-4"
          >
            <div>
              <span className="eyebrow">Public debates</span>
              <h1 className="mt-2 font-serif text-4xl tracking-tight text-charcoal sm:text-5xl">
                Explore
              </h1>
            </div>
            <Link
              to={user ? '/arena' : '/signin'}
              className="btn-primary hidden !px-5 !py-2.5 sm:inline-flex"
            >
              Create a debate
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
          </motion.div>

          {/* ── Filter bar ── */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: EASE, delay: 0.07 }}
            className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center"
          >
            {/* Search */}
            <div className="relative flex-1">
              <svg
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-muted"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search debates…"
                className="w-full rounded-full border border-black/[0.08] bg-white/80 py-2.5 pl-9 pr-9 text-sm text-charcoal placeholder-ink-muted backdrop-blur-sm focus:border-charcoal/40 focus:outline-none focus:ring-0 transition-colors"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-muted hover:text-charcoal transition-colors"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>

            {/* Status pill tabs */}
            <div className="flex gap-1 rounded-full border border-black/[0.07] bg-white/80 p-1 backdrop-blur-sm">
              {STATUS_TABS.map(tab => (
                <button
                  key={tab.value}
                  onClick={() => setStatusFilter(tab.value)}
                  className={`rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors ${
                    statusFilter === tab.value
                      ? 'bg-charcoal text-white'
                      : 'text-ink-muted hover:text-charcoal'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Sort */}
            <select
              value={sortBy}
              onChange={e => setSortBy(e.target.value)}
              className="cursor-pointer rounded-full border border-black/[0.07] bg-white/80 px-4 py-2.5 text-xs font-medium text-charcoal backdrop-blur-sm transition-colors focus:border-charcoal/40 focus:outline-none"
            >
              <option value="newest">Newest</option>
              <option value="most_voted">Most voted</option>
            </select>
          </motion.div>

          {/* ── Stats line ── */}
          {!loading && (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.4, delay: 0.15 }}
              className="mt-4 font-mono text-[11px] uppercase tracking-wider text-ink-muted"
            >
              {filteredDebates.length} {filteredDebates.length === 1 ? 'debate' : 'debates'}
              {stats.totalVotes > 0 && ` · ${stats.totalVotes.toLocaleString()} votes`}
            </motion.p>
          )}

          {/* ── Debate list (drafted compartments) ── */}
          {!loading && filteredDebates.length > 0 && (
            <div className="mt-4 divide-y divide-black/[0.06] overflow-hidden rounded-2xl border border-black/[0.08] bg-white/85 shadow-frame backdrop-blur-sm">
              <AnimatePresence mode="popLayout">
                {pageDebates.map(debate => (
                  <DebateRow
                    key={debate.id}
                    debate={debate}
                    onClick={() => navigate(`/debate/${debate.id}`)}
                  />
                ))}
              </AnimatePresence>
            </div>
          )}

          {/* ── Pagination ── */}
          {!loading && totalPages > 1 && (
            <div className="mt-10 flex items-center justify-between border-t border-hairline pt-6">
              <button
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="flex items-center gap-1.5 text-sm font-medium text-charcoal transition-colors hover:text-steel-blue disabled:cursor-not-allowed disabled:opacity-30"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Previous
              </button>
              <span className="text-xs text-ink-muted">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="flex items-center gap-1.5 text-sm font-medium text-charcoal transition-colors hover:text-steel-blue disabled:cursor-not-allowed disabled:opacity-30"
              >
                Next
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          )}

          {/* ── Loading state ── */}
          {loading && (
            <div className="flex flex-col items-center justify-center py-32">
              <div className="relative h-8 w-8">
                <div className="absolute inset-0 rounded-full border-2 border-hairline" />
                <div className="absolute inset-0 animate-spin rounded-full border-2 border-transparent border-t-charcoal" />
              </div>
              <p className="mt-5 text-sm text-ink-muted">Loading debates…</p>
            </div>
          )}

          {/* ── Empty state ── */}
          {!loading && filteredDebates.length === 0 && (
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, ease: EASE }}
              className="py-28 text-center"
            >
              {searchQuery ? (
                <>
                  <p className="font-serif text-2xl text-charcoal">No debates found</p>
                  <p className="mt-2 text-sm text-ink-muted">
                    Nothing matched{' '}
                    <span className="font-semibold text-charcoal">"{searchQuery}"</span>
                  </p>
                  <button
                    onClick={() => setSearchQuery('')}
                    className="mt-6 text-sm font-semibold text-charcoal underline underline-offset-4 hover:text-steel-blue transition-colors"
                  >
                    Clear search
                  </button>
                </>
              ) : (
                <>
                  <p className="font-serif text-2xl text-charcoal">No debates yet</p>
                  <p className="mt-2 text-sm text-ink-muted">
                    Be the first to create and share a debate.
                  </p>
                  <Link
                    to={user ? '/arena' : '/signin'}
                    className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-charcoal hover:text-steel-blue transition-colors"
                  >
                    Create a debate
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </Link>
                </>
              )}
            </motion.div>
          )}

        </div>
      </div>
    </MotionConfig>
  );
};

export default Explore;
